// ============================================================
// MongoDBSecurity.jsx
//
// A branded guide to securing MongoDB instances using RBAC,
// TLS/SSL encryption, and Network Isolation.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #FF5252  (Cyber Red / Security Alert)
//   Accent      → #00ED64  (MongoDB Green / Access Granted)
//   Text        → #FFFFFF  (White)
//   Terminal    → #010409  (High-Contrast Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from "@mui/material";

/* MUI Icons */
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import EnhancedEncryptionIcon from "@mui/icons-material/EnhancedEncryption";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ShieldIcon from "@mui/icons-material/Shield";
import LockIcon from "@mui/icons-material/Lock";
import KeyIcon from "@mui/icons-material/Key";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(255, 82, 82, 0.2)", // Security Red Border
    red: "#FF5252",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.red }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FFCDD2", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBSecurity() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.red }} />}
                        label="Infrastructure Security · Module 16"
                        sx={{ bgcolor: "rgba(255,82,82,0.1)", color: BRAND.red, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Database <span style={{ color: BRAND.red }}>Hardening</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Don't be the next headline. Secure your data using MongoDB's multi-layered security model:
                        Authentication, Authorization, Network Isolation, and Encryption.
                    </Typography>
                </Box>

                {/* 2. THE THREE PILLARS */}
                <Box sx={{ mb: 10 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <AdminPanelSettingsIcon sx={{ color: BRAND.red, fontSize: 40, mb: 2 }} />
                                <Typography variant="h6" sx={{ color: BRAND.white, mb: 1, fontWeight: 700 }}>Authentication</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Verifying "Who are you?" using SCRAM (Salted Challenge Response Authentication Mechanism) or x.509 certificates.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <VpnLockIcon sx={{ color: BRAND.red, fontSize: 40, mb: 2 }} />
                                <Typography variant="h6" sx={{ color: BRAND.white, mb: 1, fontWeight: 700 }}>Network Isolation</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Blocking "Where are you from?" using Firewalls, VPC Peering, and IP Whitelisting to restrict traffic.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <EnhancedEncryptionIcon sx={{ color: BRAND.red, fontSize: 40, mb: 2 }} />
                                <Typography variant="h6" sx={{ color: BRAND.white, mb: 1, fontWeight: 700 }}>Encryption</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Scrambling data "In-Transit" (TLS/SSL) and "At-Rest" (AES-256) so it's unreadable if stolen.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. ROLE-BASED ACCESS CONTROL (RBAC) */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <ShieldIcon sx={{ color: BRAND.red, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Role-Based Access (RBAC)</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        The <strong>Principle of Least Privilege</strong> dictates that a user should only have the permissions
                        absolutely necessary for their job. Never use the "Root" user for your application connection.
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal
                                label="mongosh: Creating a Read-Only User"
                                code={`use admin;\n\ndb.createUser({\n  user: "reportsUser",\n  pwd: passwordPrompt(), \n  roles: [\n    { role: "read", db: "salesData" },\n    { role: "readWrite", db: "inventory" }\n  ]\n});`}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <List>
                                {[
                                    { icon: <LockIcon />, text: "read: Can only view data." },
                                    { icon: <KeyIcon />, text: "readWrite: Can insert and modify documents." },
                                    { icon: <AdminPanelSettingsIcon />, text: "dbAdmin: Can manage indexes and stats." },
                                    { icon: <ShieldIcon />, text: "userAdmin: Can manage roles and users." }
                                ].map((role, i) => (
                                    <ListItem key={i} sx={{ px: 0 }}>
                                        <ListItemIcon sx={{ color: BRAND.red, minWidth: 40 }}>{role.icon}</ListItemIcon>
                                        <ListItemText primary={role.text} primaryTypographyProps={{ variant: 'body2', color: BRAND.muted }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    </Grid>
                </Box>



                {/* 4. SECURITY CHECKLIST */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 4, bgcolor: "rgba(255, 82, 82, 0.05)", border: `1px solid ${BRAND.red}`, borderRadius: 4 }}>
                        <Typography variant="h6" sx={{ color: BRAND.red, mb: 3, fontWeight: 800 }}>Production Hardening Checklist</Typography>
                        <Grid container spacing={2}>
                            {[
                                "Enable Access Control (Auth is NOT on by default locally!)",
                                "Use TLS/SSL for all client-to-server traffic.",
                                "Bind MongoDB to private IPs only (disable 0.0.0.0).",
                                "Change the default port (27017) to obscure the service.",
                                "Disable the 'HTTP Interface' and 'REST API' if unused.",
                                "Rotate database passwords and keys every 90 days."
                            ].map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <DoneIcon sx={{ color: BRAND.green, fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ color: BRAND.white }}>{item}</Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #440000)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <VpnLockIcon style={{ fontSize: 40, color: BRAND.red, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Instance Secured?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Security is a process, not a destination. Review your logs regularly to detect unauthorized access attempts.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.red, color: BRAND.white, fontWeight: 900 }}>Audit Permissions</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Security Whitepaper</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
